// src/services/aeo/scanDraft.ts
//
// The scan draft service: the 50 questions are drafted and reviewed before the
// paid scan runs. Contract: ./scanDraftTypes.ts. Every call returns a
// ScanDraftResult and never throws.
//
// BACKEND CONTRACT (proposed, not live yet; camelCase bodies, `version` in every write):
//
//   GET    /scan-drafts?domain=&businessId=&open=true          -> { drafts: ScanDraft[] } (at most one)
//   GET    /scan-drafts?status=open                             -> { drafts: ScanDraftListItem[] } (every open draft; handoff)
//   POST   /scan-drafts                                         -> { draft, reused }
//   GET    /scan-drafts/{id}                                    -> ScanDraft
//   PATCH  /scan-drafts/{id}/business   {...patch, version}     -> { draft, effect }   (flat body)
//   POST   /scan-drafts/{id}/questions  {refreshWords, refreshDemand} -> { draft, effect }   (status "building")
//   PATCH  /scan-drafts/{id}/questions/{n}                      -> { draft, effect }
//   POST   /scan-drafts/{id}/questions/{n}/alternatives         -> { alternatives }
//   POST   /scan-drafts/{id}/questions/{n}/alternatives/{altId}/accept -> { draft, effect }
//   PATCH  /scan-drafts/{id}/words                              -> { draft, effect }
//   POST   /scan-drafts/{id}/decisions/{decisionId}  {choice}   -> { draft, effect }
//          first_check "For the plan" cards also take {choice: "recommended", values} (the edited list, H5-a)
//   POST   /scan-drafts/{id}/recipe        {mode, picks}        -> { draft, effect }
//   POST   /scan-drafts/{id}/undo | /approve | /reopen          -> { draft, effect }
//   POST   /scan-drafts/{id}/start         {note, requestedBy}  -> StartScanDraftResult (idempotent)
//   DELETE /scan-drafts/{id}?version=                            -> 204   (version optional)
//   POST   /scan-drafts/{id}/search-counts/refresh              -> { draft, effect }   (202 while pulling)
//   POST   /detect                                              -> outlets, buyGuess, buyWhy, market, pages when served;
//                                                                  v4.5: signals, marketsGuess, sellsToBusinessesGuess, partnerProductsGuess
//   POST   /businesses/{domain}/connectors/gsc/connect {return_to: "new-scan:<draftId|domain>"}
//   GET    /businesses/{id}                                     -> { ..., recipeUpdate }
//
// Refusals map to ScanDraftFailure: a 404 whose errors[0] is `not_found` is
// refused ("This draft no longer exists." on draft routes); any other 404
// (FastAPI's {"detail":"Not Found"} for a route that is not served,
// `kind_not_served`) and every 405 are absent, with ABSENT's sentence; 409 stale_draft stale, 409
// draft_exists / business_exists exists (with data), 422 lint_failed lint, any
// other 4xx refused with the backend's sentence, no answer at all network.
//
// v4.5 brand mode (SPEC-v45 §11, handoffs 1 to 5): the business carries
// `mode` (derived from `buy` here and on the server; the server value wins),
// and on Online `markets` (1 to 4 codes, priority order, home last),
// `sellsToBusinesses`, `partnerProducts` and `outlets: []`, with `market` the
// home market's label for older readers. A brand draft comes back with
// `counts.check` brand keys, question rows carrying `market` and `trace`, the
// `pulling_demand` job step and `draft.demand`; all are read as sent.
// `refreshDemand` on Draft again pulls the Google searches again.
//
// Where the calls go is flags.ts: live with VITE_ENABLE_OURA_SCAN_DRAFT_LIVE,
// the fixture engine (./scanDraftMock.ts) on a local dev run, else `absent`.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { connectConnector, getBusinessConnectors, listBusinesses } from "@service/aeo";
import type { AeoConnectPayload } from "@service/aeo";
import { scanDraftMode } from "@oura/newscan/flags";
import { getOnboarding } from "./onboarding";
import { scanDraftMock } from "./scanDraftMock";
import { scanDraftModeFor } from "./scanDraftTypes";
import type {
    BuyMode,
    CreateScanDraftRequest,
    DecisionChoice,
    QuestionPatch,
    ScanDraft,
    ScanDraftAlternative,
    ScanDraftBusiness,
    ScanDraftDetected,
    ScanDraftEffect,
    ScanDraftFailure,
    ScanDraftKind,
    ScanDraftListItem,
    ScanDraftMarket,
    ScanDraftPurpose,
    ScanMarketCode,
    ScanDraftResult,
    ScanDraftStatus,
    ScanDraftWrite,
    StartScanDraftRequest,
    StartScanDraftResult,
    StartedScanDraft,
} from "./scanDraftTypes";

export { scanDraftMode } from "@oura/newscan/flags";
export type { ScanDraftMode } from "@oura/newscan/flags";

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const enc = encodeURIComponent;
const NETWORK_MESSAGE = "Check your connection and try again.";
const ABSENT: ScanDraftFailure = { ok: false, kind: "absent", message: "The new scan is not available here yet." };

export type SearchConsoleStatus = "connected" | "not_connected" | "action_needed";
export type RecipeMode = "take_all" | "keep_all" | "choose_each";
/** Draft again: `refreshWords` re-runs the word research; `refreshDemand` pulls the Google searches again (brand drafts). */
export type RebuildOptions = { refreshWords?: boolean; refreshDemand?: boolean };

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ------------------------------------------------------------------------- *
 * Reading answers and refusals
 * ------------------------------------------------------------------------- */

type Body = Record<string, unknown>;

const isBody = (v: unknown): v is Body => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

/** The envelope's `data`, or the body itself when there is no envelope. */
function unwrap(raw: unknown): unknown {
    return isBody(raw) && "data" in raw ? raw.data : raw;
}

function isDraft(v: unknown): v is ScanDraft {
    return isBody(v) && typeof v.id === "string" && Array.isArray(v.questions) && typeof v.version === "number";
}

function isEffect(v: unknown): v is ScanDraftEffect {
    return isBody(v) && typeof v.label === "string" && Array.isArray(v.changed) && Array.isArray(v.skipped);
}

/** The backend's own sentence: StandardResponse `message`, or FastAPI `detail`. */
function sentence(body: Body): string | undefined {
    const message = str(body.message);
    if (message) return message;
    const detail = body.detail;
    if (typeof detail === "string") return str(detail);
    if (isBody(detail)) return str(detail.message);
    if (Array.isArray(detail)) {
        const parts = detail.map((d) => (isBody(d) ? str(d.msg) : undefined)).filter((m): m is string => Boolean(m));
        return parts.length ? parts.join("; ") : undefined;
    }
    return undefined;
}

/** The stable refusal code: `errors[0]`, or a `code` string on the body, its detail or its data. */
function codeOf(body: Body): string | undefined {
    if (Array.isArray(body.errors)) {
        const first = str(body.errors[0]);
        if (first) return first;
    }
    const detail = body.detail;
    const data = body.data;
    return str(body.code) ?? (isBody(detail) ? str(detail.code) : undefined) ?? (isBody(data) ? str(data.code) : undefined);
}

/**
 * The site preflight's refusal in words staff can act on (#4356). The backend answers
 * 400 "Domain failed preflight" with `data.reason_code` (aeo_worker.py SITE_*) and
 * `data.reason`; the bare message said nothing about what to do. Undefined for any
 * other answer, or a code this list does not know: the server's own message stays.
 */
export function preflightSentence(body: Body): { code: string; message: string } | undefined {
    const data = isBody(body.data) ? body.data : undefined;
    const code = data ? str(data.reason_code) : undefined;
    if (!data || !code) return undefined;
    const site = str(data.domain) ?? "The website";
    const reason = str(data.reason);
    const free = "Nothing was charged.";
    const sentences: Record<string, string> = {
        SITE_UNREACHABLE: `${site} did not answer our server. Check it opens, then try again. ${free}`,
        SITE_ERROR: `${site} answered with an error${reason ? ` (${reason})` : ""}. Try again in a minute; if it keeps failing, the site may be blocking our server. ${free}`,
        SITE_BLOCKS_AUTOMATED_ACCESS: `${site} is online but blocks automated visits, so it cannot be scanned. Ask the client to allow it, then try again. ${free}`,
        SITE_NOT_PUBLIC: `${site} points inside a private network, so it cannot be scanned.`,
    };
    const message = sentences[code];
    return message ? { code, message } : undefined;
}

function existsData(body: Body): ScanDraftFailure["data"] {
    const d = isBody(body.data) ? body.data : isBody(body.detail) ? body.detail : body;
    return {
        draftId: str(d.draftId) ?? str(d.draft_id),
        businessId: str(d.businessId) ?? str(d.business_id),
        businessName: str(d.businessName) ?? str(d.business_name),
    };
}

const DRAFT_GONE = "This draft no longer exists.";

/**
 * A failed request as a ScanDraftFailure. Only unexpected failures are logged.
 * `notFound` is the sentence for a 404 `not_found` (the id the route names is
 * unknown); without it the backend's own sentence is shown.
 */
function failure(err: unknown, fallback: string, where: string, notFound?: string): ScanDraftFailure {
    const res = (err as { response?: { status?: number; data?: unknown } }).response;
    if (!res) {
        catchErrorLog(`[oura] scanDraft ${where} failed with no response: ${err}`);
        return { ok: false, kind: "network", message: NETWORK_MESSAGE };
    }
    const status = res.status ?? 0;
    const body = isBody(res.data) ? res.data : {};
    const code = codeOf(body);
    const message = sentence(body) ?? fallback;
    if (status === 404 && code === "not_found") return { ok: false, kind: "refused", message: notFound ?? message, code };
    // A route that is not served answers FastAPI's bare "Not Found": never shown.
    if (status === 404 || status === 405) return { ok: false, kind: "absent", message: ABSENT.message, code };
    if (status === 409 && code === "stale_draft") return { ok: false, kind: "stale", message, code };
    if (status === 409 && (code === "draft_exists" || code === "business_exists")) return { ok: false, kind: "exists", message, code, data: existsData(body) };
    if (status === 422 && code === "lint_failed") return { ok: false, kind: "lint", message, code };
    // The site preflight (Start, and any call that runs it): say which failure, and what to do (#4356).
    const preflight = status === 400 ? preflightSentence(body) : undefined;
    if (preflight) return { ok: false, kind: "refused", message: preflight.message, code: preflight.code };
    if (status >= 500 || status < 400) catchErrorLog(`[oura] scanDraft ${where} failed (HTTP ${status}): ${message}`);
    return { ok: false, kind: "refused", message, code };
}

/** A write's answer: `{ draft, effect }`, or a bare draft, or (anything else) the draft read again. */
async function toWrite(id: string, raw: unknown): Promise<ScanDraftResult<ScanDraftWrite>> {
    const body = unwrap(raw);
    if (isBody(body) && isDraft(body.draft)) return { ok: true, data: { draft: body.draft, effect: isEffect(body.effect) ? body.effect : null } };
    if (isDraft(body)) return { ok: true, data: { draft: body, effect: null } };
    const effect = isBody(body) && isEffect(body.effect) ? body.effect : null;
    const read = await getScanDraft(id);
    return read.ok ? { ok: true, data: { draft: read.data, effect } } : read;
}

/** One live write: POST/PATCH with the version, answer read as a ScanDraftWrite. */
async function liveWrite(
    method: "post" | "patch",
    id: string,
    path: string,
    body: Body,
    fallback: string,
    where: string,
): Promise<ScanDraftResult<ScanDraftWrite>> {
    try {
        const res = await axios.request({ method, url: `${AEO_BASE}/scan-drafts/${enc(id)}${path}`, data: body, headers: authHeaders() });
        return await toWrite(id, res.data);
    } catch (err) {
        return failure(err, fallback, where, DRAFT_GONE);
    }
}

const SAVE_FAILED = "That change was not saved.";

/** Where a call goes (flags.ts): nowhere, the fixture engine, or the backend. Only the chosen side runs. */
function route<T>(fixture: () => Promise<ScanDraftResult<T>>, live: () => Promise<ScanDraftResult<T>>): Promise<ScanDraftResult<T>> {
    const mode = scanDraftMode();
    if (mode === "absent") return Promise.resolve(ABSENT);
    return mode === "fixtures" ? fixture() : live();
}

/* ------------------------------------------------------------------------- *
 * Reads
 * ------------------------------------------------------------------------- */

/** What an older /detect read: the four standard pages, shown with one spinner. */
const STANDARD_PAGES = ["Home page", "Main pages", "Contact page", "About page"];

/** The `pages` kinds /detect reads, as the progress list words them. */
const PAGE_LABELS: Record<string, string> = {
    home: "Home page",
    services: "Services page",
    about: "About page",
    outlets: "Outlets page",
};

const BUY_MODES: ReadonlyArray<BuyMode> = ["visit", "online", "both", "we_go_to_them"];

/** v4.5 markets: the code and the label written in a question. `global` is never a market a business lists. */
const MARKET_LABELS: Record<Exclude<ScanMarketCode, "global">, string> = {
    us: "the US",
    sg: "Singapore",
    my: "Malaysia",
    au: "Australia",
    uk: "the UK",
};
const MARKETS: ReadonlyArray<string> = ["Malaysia", "Singapore", "Australia", "the US", "the UK"];

/** A market as /detect or a stored draft may write it: a code ("us"), a label ("the US") or { code, label }. */
function toMarket(v: unknown): ScanDraftMarket | null {
    const raw = isBody(v) ? str(v.code) ?? str(v.label) : str(v);
    if (!raw) return null;
    const key = raw.trim().toLowerCase().replace(/^the /, "");
    const hit = (Object.keys(MARKET_LABELS) as Array<keyof typeof MARKET_LABELS>).find(
        (c) => c === key || MARKET_LABELS[c].toLowerCase().replace(/^the /, "") === key,
    );
    return hit ? { code: hit, label: MARKET_LABELS[hit] } : null;
}

const pageLabel = (kind: string): string => PAGE_LABELS[kind] ?? `${kind.charAt(0).toUpperCase()}${kind.slice(1)} page`;
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean) : []);

/** The read of a site the read could not use: empty, low confidence, so staff fill the Business step by hand. */
function emptyRead(reason: string | null): ScanDraftDetected {
    return {
        businessName: "",
        category: "",
        city: "",
        outlets: [],
        services: [],
        buyGuess: null,
        buyWhy: null,
        market: null,
        confidence: "low",
        reason,
        pages: STANDARD_PAGES,
    };
}

/**
 * POST /detect's answer as ScanDraftDetected. The newer keys (`outlets`,
 * `buyGuess`, `buyWhy`, `market`, `pages`) are read when present; an older
 * backend falls back to `anchors.primary` as outlet names with an empty town
 * (the Business step fills the town from the city). `primary` is never read as
 * area-then-town pairs.
 */
function toDetected(raw: unknown): ScanDraftDetected {
    const d: Body = isBody(raw) ? raw : {};
    const anchors: Body = isBody(d.anchors) ? d.anchors : {};
    const name = (str(d.business_name) ?? "").trim();
    const city = (str(anchors.city) ?? "").trim();

    let outlets: { name: string; town: string }[];
    if (Array.isArray(d.outlets)) {
        const seen = new Set<string>();
        outlets = d.outlets.flatMap((row) => {
            if (!isBody(row)) return [];
            const outlet = (str(row.name) ?? "").trim();
            if (!outlet || seen.has(outlet.toLowerCase())) return [];
            seen.add(outlet.toLowerCase());
            return [{ name: outlet, town: (str(row.town) ?? "").trim() }];
        });
    } else {
        // `primary` is seeded with the business name when the read found no area: never an outlet.
        outlets = strings(anchors.primary)
            .filter((a) => a.toLowerCase() !== name.toLowerCase())
            .map((a) => ({ name: a, town: "" }));
    }

    const buy = BUY_MODES.find((m) => m === d.buyGuess) ?? null;
    const why = buy ? (str(d.buyWhy)?.trim() ?? null) : null;
    const market = MARKETS.find((m) => m === d.market) ?? toMarket(d.market)?.label ?? null;
    const pages = Array.isArray(d.pages) ? strings(d.pages).map(pageLabel) : STANDARD_PAGES;

    // v4.5 brand signals, read only when sent (an older backend sends none).
    const seen = new Set<string>();
    const marketsGuess = Array.isArray(d.marketsGuess)
        ? d.marketsGuess.map(toMarket).filter((m): m is ScanDraftMarket => m !== null && !seen.has(m.code) && Boolean(seen.add(m.code)))
        : undefined;
    const brandFields: Partial<ScanDraftDetected> = {
        ...(Array.isArray(d.signals) ? { signals: strings(d.signals) } : {}),
        ...(marketsGuess ? { marketsGuess } : {}),
        ...("sellsToBusinessesGuess" in d ? { sellsToBusinessesGuess: typeof d.sellsToBusinessesGuess === "boolean" ? d.sellsToBusinessesGuess : null } : {}),
        ...(Array.isArray(d.partnerProductsGuess) ? { partnerProductsGuess: strings(d.partnerProductsGuess) } : {}),
    };

    return {
        businessName: name,
        category: (str(d.category) ?? "").trim(),
        city,
        outlets,
        services: strings(d.services),
        buyGuess: buy,
        buyWhy: why,
        market,
        confidence: d.confidence === "high" ? "high" : "low",
        reason: str(d.reason)?.trim() ?? null,
        pages,
        ...brandFields,
    };
}

/**
 * The website read for the Business step (POST /detect). A 4xx (an invalid
 * hostname, a site that is not public) is refused with the backend's
 * sentence; anything else that fails reads as an empty low-confidence read,
 * as the existing detect() does, so staff can still fill the details by hand.
 */
export function readSite(domain: string): Promise<ScanDraftResult<ScanDraftDetected>> {
    return route(
        () => scanDraftMock.readSite(domain),
        async () => {
        try {
            const res = await axios.post(`${AEO_BASE}/detect`, { domain }, { headers: authHeaders() });
            return { ok: true, data: toDetected(unwrap(res.data)) };
        } catch (err) {
            const status = (err as { response?: { status?: number } }).response?.status ?? 0;
            if (status >= 400 && status < 500 && status !== 404 && status !== 405) return failure(err, "This site could not be read.", "readSite");
            catchErrorLog(`[oura] scanDraft readSite failed for ${domain} (HTTP ${status || "none"}): ${err}`);
            return { ok: true, data: emptyRead(null) };
        }
    },
    );
}

/** The open draft for a site or a business, if there is one. Live reads `{ drafts: [...] }` and takes the first. */
export function findOpenScanDraft(where: { domain?: string; businessId?: string }): Promise<ScanDraftResult<ScanDraft | null>> {
    return route(
        () => scanDraftMock.findOpen(where),
        async () => {
        // The route needs one of the two (422 invalid_query otherwise): with neither there is nothing to find.
        if (!where.domain && !where.businessId) return { ok: true, data: null };
        try {
            const res = await axios.get(`${AEO_BASE}/scan-drafts`, {
                headers: authHeaders(),
                params: { ...(where.domain ? { domain: where.domain } : {}), ...(where.businessId ? { businessId: where.businessId } : {}), open: true },
            });
            const body = unwrap(res.data);
            const list: unknown[] = isBody(body) && Array.isArray(body.drafts) ? body.drafts : Array.isArray(body) ? body : [];
            const first = list.find(isDraft);
            return { ok: true, data: first ?? null };
        } catch (err) {
            return failure(err, "The open drafts could not be read.", "findOpenScanDraft");
        }
    },
    );
}

const OPEN_STATUSES: ReadonlyArray<ScanDraftStatus> = ["building", "ready", "approved", "failed"];
const DRAFT_KINDS: ReadonlyArray<ScanDraftKind> = ["new_scan", "edit_set", "recipe_change"];
const DRAFT_PURPOSES: ReadonlyArray<ScanDraftPurpose> = ["new_scan", "first_check", "edit_set", "recipe_change"];

/** One row of the open-drafts list: a full draft or a summary, camelCase or snake_case. Null when unusable. */
function toListItem(v: unknown): ScanDraftListItem | null {
    if (!isBody(v)) return null;
    const id = str(v.id);
    const kind = DRAFT_KINDS.find((k) => k === v.kind);
    const status = OPEN_STATUSES.find((s) => s === v.status);
    const domain = str(v.domain);
    if (!id || !kind || !status || !domain) return null;
    const business = isBody(v.business) ? v.business : {};
    // `purpose` stays null unless the server named one we know: an unknown value reads as the kind's own.
    const purpose = DRAFT_PURPOSES.find((p) => p === v.purpose) ?? null;
    return {
        id,
        kind,
        purpose,
        domain,
        businessName: str(v.businessName) ?? str(v.business_name) ?? str(business.businessName) ?? "",
        businessId: str(v.businessId) ?? str(v.business_id) ?? null,
        status,
        createdAt: str(v.createdAt) ?? str(v.created_at) ?? "",
        updatedAt: str(v.updatedAt) ?? str(v.updated_at) ?? "",
    };
}

/**
 * Every open draft, newest first, so staff can pick one up without retyping
 * its site. Live: GET /scan-drafts?status=open (backend handoff); a backend
 * without the route answers an empty list.
 */
export function listOpenScanDrafts(): Promise<ScanDraftResult<ScanDraftListItem[]>> {
    return route(
        () => scanDraftMock.listOpen(),
        async () => {
            try {
                const res = await axios.get(`${AEO_BASE}/scan-drafts`, { headers: authHeaders(), params: { status: "open" } });
                const body = unwrap(res.data);
                const list: unknown[] = isBody(body) && Array.isArray(body.drafts) ? body.drafts : Array.isArray(body) ? body : [];
                const items = list.map(toListItem).filter((d): d is ScanDraftListItem => d !== null);
                items.sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : b.updatedAt < a.updatedAt ? -1 : 0));
                return { ok: true, data: items };
            } catch (err) {
                const fail = failure(err, "The open drafts could not be read.", "listOpenScanDrafts");
                // Not served yet, or a backend that still wants a domain or business: nothing to list.
                return fail.kind === "absent" || fail.code === "invalid_query" ? { ok: true, data: [] } : fail;
            }
        },
    );
}

export function getScanDraft(id: string): Promise<ScanDraftResult<ScanDraft>> {
    return route(
        () => scanDraftMock.get(id),
        async () => {
        try {
            const res = await axios.get(`${AEO_BASE}/scan-drafts/${enc(id)}`, { headers: authHeaders() });
            const body = unwrap(res.data);
            return isDraft(body) ? { ok: true, data: body } : { ok: false, kind: "refused", message: "The draft came back unreadable." };
        } catch (err) {
            return failure(err, "The draft could not be read.", "getScanDraft", DRAFT_GONE);
        }
    },
    );
}

/* ------------------------------------------------------------------------- *
 * Create and edit
 * ------------------------------------------------------------------------- */

/**
 * The business as sent (handoff 2, v4.5): `mode` from `buy`, and on Online
 * `market` as the home market's label (the last of `markets`) for older
 * readers. Outlets go as the Business step built them (it sends none on
 * Online); the brand fields stay on a local business untouched.
 */
function withMode(b: ScanDraftBusiness): ScanDraftBusiness {
    const next: ScanDraftBusiness = { ...b, mode: scanDraftModeFor(b.buy) };
    if (b.buy !== "online") return next;
    const home = b.markets?.length ? b.markets[b.markets.length - 1] : null;
    return { ...next, market: home ? home.label : b.market ?? null };
}

export function createScanDraft(req: CreateScanDraftRequest): Promise<ScanDraftResult<{ draft: ScanDraft; reused: boolean }>> {
    // A first_check request carries no inputs: the server drafts them from the pitch, the form and the quote (H3-a).
    const sent: CreateScanDraftRequest = "inputs" in req ? { ...req, inputs: withMode(req.inputs) } : req;
    return route(
        () => scanDraftMock.create(sent),
        async () => {
        try {
            const res = await axios.post(`${AEO_BASE}/scan-drafts`, sent, { headers: authHeaders() });
            const body = unwrap(res.data);
            const reused = isBody(body) && body.reused === true;
            if (isBody(body) && isDraft(body.draft)) return { ok: true, data: { draft: body.draft, reused } };
            if (isDraft(body)) return { ok: true, data: { draft: body, reused } };
            const draftId = isBody(body) ? str(body.draftId) ?? str(body.id) : undefined;
            if (!draftId) return { ok: false, kind: "refused", message: "The draft was not created." };
            const read = await getScanDraft(draftId);
            return read.ok ? { ok: true, data: { draft: read.data, reused } } : read;
        } catch (err) {
            return failure(err, "The draft could not be started.", "createScanDraft");
        }
    },
    );
}

export function patchScanDraftBusiness(id: string, version: number, patch: Partial<ScanDraftBusiness>): Promise<ScanDraftResult<ScanDraftWrite>> {
    // A new buy answer carries its mode.
    const sent: Partial<ScanDraftBusiness> = patch.buy ? { ...patch, mode: scanDraftModeFor(patch.buy) } : { ...patch };
    // New markets carry the deprecated home market too, the way withMode() does on create, so an
    // older reader of `market` never sees a different home market from the list (Jack, #4285/#4291).
    if (patch.markets !== undefined) {
        const home = patch.markets.length ? patch.markets[patch.markets.length - 1] : null;
        sent.market = home?.label ?? null;
    }
    return route(
        () => scanDraftMock.patchBusiness(id, version, sent),
        () => liveWrite("patch", id, "/business", { ...sent, version }, SAVE_FAILED, "patchScanDraftBusiness"),
    );
}

/**
 * Draft the 50 again from the Business step. `refreshWords` also re-runs the
 * two AI calls; `refreshDemand` (brand drafts) pulls the Google searches again
 * instead of reusing the stored pull. Status becomes "building".
 */
export function rebuildScanDraft(id: string, version: number, opts?: RebuildOptions): Promise<ScanDraftResult<ScanDraftWrite>> {
    // `refreshDemand` is only sent when asked for, so a backend before v4.5 sees the body it knows.
    const body: Body = { version, refreshWords: opts?.refreshWords === true, ...(opts?.refreshDemand ? { refreshDemand: true } : {}) };
    return route(
        () => scanDraftMock.rebuild(id, version, opts),
        () => liveWrite("post", id, "/questions", body, "The draft could not be written again.", "rebuildScanDraft"),
    );
}

export function patchScanDraftQuestion(id: string, version: number, n: number, patch: QuestionPatch): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.patchQuestion(id, version, n, patch),
        () => liveWrite("patch", id, `/questions/${n}`, { ...patch, version }, SAVE_FAILED, "patchScanDraftQuestion"),
    );
}

/** "Try another": up to 3, deterministic, no spend. An empty list is a valid answer. */
export function listQuestionAlternatives(id: string, n: number): Promise<ScanDraftResult<ScanDraftAlternative[]>> {
    return route(
        () => scanDraftMock.alternatives(id, n),
        async () => {
        try {
            const res = await axios.post(`${AEO_BASE}/scan-drafts/${enc(id)}/questions/${n}/alternatives`, {}, { headers: authHeaders() });
            const body = unwrap(res.data);
            const list = Array.isArray(body) ? body : isBody(body) && Array.isArray(body.alternatives) ? body.alternatives : [];
            const alternatives = list.filter(
                (a): a is ScanDraftAlternative => isBody(a) && typeof a.id === "string" && typeof a.text === "string" && typeof a.why === "string",
            );
            return { ok: true, data: alternatives };
        } catch (err) {
            return failure(err, "No other wording could be found.", "listQuestionAlternatives", DRAFT_GONE);
        }
    },
    );
}

export function acceptQuestionAlternative(id: string, version: number, n: number, altId: string): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.acceptAlternative(id, version, n, altId),
        () => liveWrite("post", id, `/questions/${n}/alternatives/${enc(altId)}/accept`, { version }, SAVE_FAILED, "acceptQuestionAlternative"),
    );
}

/** Change one word everywhere it is used. Locked questions keep their wording and come back in `effect.skipped`. */
export function patchScanDraftWord(id: string, version: number, variable: string, value: string): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.patchWord(id, version, variable, value),
        () => liveWrite("patch", id, "/words", { version, variable, value }, SAVE_FAILED, "patchScanDraftWord"),
    );
}

/** Answer a decision with a server-side option. Client-side options (open_editor, open_word, connect_gsc, choose_each) never come here. */
export function answerScanDraftDecision(id: string, version: number, decisionId: string, choice: DecisionChoice): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.answer(id, version, decisionId, choice),
        () => liveWrite("post", id, `/decisions/${enc(decisionId)}`, { version, choice }, SAVE_FAILED, "answerScanDraftDecision"),
    );
}

/**
 * A first_check "For the plan" card answered with an edited list
 * (`plan_keywords`: 5 to 10 phrases; `plan_outlet_names`: the names). Same
 * route as any answer, with the list as `values`. Allowed again on an answered
 * card, to change the list before Approve.
 */
export function answerScanDraftPlan(id: string, version: number, decisionId: string, values: string[]): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.answerPlan(id, version, decisionId, values),
        () => liveWrite("post", id, `/decisions/${enc(decisionId)}`, { version, choice: "recommended", values }, SAVE_FAILED, "answerScanDraftPlan"),
    );
}

export function setScanDraftRecipe(
    id: string,
    version: number,
    mode: RecipeMode,
    picks?: Record<number, "take" | "keep">,
): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.setRecipe(id, version, mode, picks),
        () => liveWrite("post", id, "/recipe", { version, mode, ...(picks ? { picks } : {}) }, SAVE_FAILED, "setScanDraftRecipe"),
    );
}

export function undoScanDraft(id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(() => scanDraftMock.undo(id, version), () => liveWrite("post", id, "/undo", { version }, "That could not be undone.", "undoScanDraft"));
}

export function approveScanDraft(id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.approve(id, version),
        () => liveWrite("post", id, "/approve", { version }, "The 50 could not be approved.", "approveScanDraft"),
    );
}

export function reopenScanDraft(id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.reopen(id, version),
        () => liveWrite("post", id, "/reopen", { version }, "The draft could not be reopened.", "reopenScanDraft"),
    );
}

/* ------------------------------------------------------------------------- *
 * Start, discard, search counts
 * ------------------------------------------------------------------------- */

/** Start the paid scan from an approved draft. Idempotent: a repeat returns the same assessment with `reused`. */
export function startScanDraft(id: string, version: number, req: StartScanDraftRequest): Promise<ScanDraftResult<StartScanDraftResult>> {
    return route(
        () => scanDraftMock.start(id, version, req),
        async () => {
        try {
            const res = await axios.post(`${AEO_BASE}/scan-drafts/${enc(id)}/start`, { version, ...req }, { headers: authHeaders() });
            const body = unwrap(res.data);
            const b: Body = isBody(body) ? body : {};
            const assessmentId = str(b.assessmentId) ?? str(b.assessment_id);
            // Applied at the next monthly check (H2-c): no run, the day it applies instead.
            const scheduledFor = str(b.scheduledFor) ?? str(b.scheduled_for) ?? null;
            if (!assessmentId && !scheduledFor) return { ok: false, kind: "refused", message: "The scan did not start." };
            const minutes = b.estimatedMinutes ?? b.estimated_duration_minutes;
            return {
                ok: true,
                data: {
                    assessmentId: assessmentId ?? "",
                    draftId: str(b.draftId) ?? id,
                    businessId: str(b.businessId) ?? str(b.business_id) ?? null,
                    estimatedMinutes: typeof minutes === "number" ? minutes : 40,
                    reused: b.reused === true,
                    scheduledFor,
                    tookOverPitch: b.tookOverPitch === true || b.took_over_pitch === true,
                },
            };
        } catch (err) {
            return failure(err, "The scan could not be started.", "startScanDraft", DRAFT_GONE);
        }
    },
    );
}

/**
 * Scans a draft started in the last 30 minutes, newest first. Fixtures only:
 * live, the started run is a scan job (rehydrateScanJobs feeds useScanJobs),
 * so this answers an empty list there and where the service is absent.
 */
export function listStartedScanDrafts(): Promise<ScanDraftResult<StartedScanDraft[]>> {
    if (scanDraftMode() !== "fixtures") return Promise.resolve({ ok: true, data: [] });
    return scanDraftMock.listStarted();
}

/** Discard (soft). `version` is required and checked: an old one is refused `stale` (ourteam-backend#6080). */
export function discardScanDraft(id: string, version: number): Promise<ScanDraftResult<null>> {
    return route(
        () => scanDraftMock.discard(id, version),
        async () => {
        try {
            await axios.delete(`${AEO_BASE}/scan-drafts/${enc(id)}`, {
                headers: authHeaders(),
                params: { version },
            });
            return { ok: true, data: null };
        } catch (err) {
            return failure(err, "The draft could not be discarded.", "discardScanDraft", DRAFT_GONE);
        }
    },
    );
}

/** Read the Search Console counts again (after a connect, or a deep pull). */
export function refreshScanDraftSearch(id: string): Promise<ScanDraftResult<ScanDraftWrite>> {
    return route(
        () => scanDraftMock.refreshSearch(id),
        async () => {
        try {
            const res = await axios.post(`${AEO_BASE}/scan-drafts/${enc(id)}/search-counts/refresh`, {}, { headers: authHeaders() });
            return await toWrite(id, res.data);
        } catch (err) {
            return failure(err, "The search counts could not be read.", "refreshScanDraftSearch", DRAFT_GONE);
        }
    },
    );
}

/* ------------------------------------------------------------------------- *
 * Search Console and the businesses list (existing routes; a bare domain works before the business exists)
 * ------------------------------------------------------------------------- */

export function getSearchConsoleState(domain: string): Promise<ScanDraftResult<{ status: SearchConsoleStatus; property: string | null }>> {
    return route(
        () => scanDraftMock.searchConsoleState(domain),
        async () => {
        const connectors = await getBusinessConnectors(enc(domain));
        const gsc = connectors.find((c) => c.key === "gsc");
        return { ok: true, data: { status: gsc?.status ?? "not_connected", property: gsc?.propertyUrl ?? null } };
    },
    );
}

const siteKey = (domain: string) =>
    domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/+$/, "");

const RETURN_DRAFT = /^[0-9a-f-]{36}$/;
const RETURN_DOMAIN = /^[a-z0-9.-]{1,253}$/;

/**
 * Where Google's consent sends the browser back to. A
 * draft id when there is one, else the bare domain (the backend then finds
 * the domain's open draft). Null when neither fits the backend's pattern
 * (a path-scoped site): the connect still works, the return is the default.
 */
function returnTo(domain: string, draftId?: string | null): string | null {
    if (draftId && RETURN_DRAFT.test(draftId)) return `new-scan:${draftId}`;
    const key = siteKey(domain);
    return RETURN_DOMAIN.test(key) ? `new-scan:${key}` : null;
}

/**
 * Live: the Google consent URL to open in the tab the click opened, with
 * `return_to` so consent comes back to the New scan flow. An older backend
 * that refuses the field is asked once more without it. Fixtures: null, and it
 * reads as connected about 1.2 s later.
 */
export function connectSearchConsole(
    domain: string,
    opts?: { draftId?: string | null },
): Promise<ScanDraftResult<{ redirectUrl: string | null }>> {
    return route(
        () => scanDraftMock.connectSearchConsole(domain),
        async () => {
        const ret = returnTo(domain, opts?.draftId);
        // `return_to` is not on AeoConnectPayload yet: the backend is asked to add it.
        const payload: AeoConnectPayload & { return_to?: string } = ret ? { return_to: ret } : {};
        let r = await connectConnector(enc(domain), "gsc", payload);
        // Retry bare only when the server answered (axios network codes look like ECONNABORTED / ERR_NETWORK).
        if (!r.ok && ret && !/^(E[A-Z]+|ERR_[A-Z_]+)$/.test(r.code ?? "")) r = await connectConnector(enc(domain), "gsc");
        if (!r.ok) return { ok: false, kind: "refused", message: r.message, code: r.code };
        return { ok: true, data: { redirectUrl: r.kind === "redirect" ? r.url : null } };
    },
    );
}

/**
 * Where a business that owns a site stands (JOURNEY-SPEC §2.6):
 * `prospect` scanned or drafted, not signed; `onboarding` signed and its first
 * AI check not finished; `client` past its first check, or signed before
 * onboarding existed.
 */
export type SiteOwnerStage = "prospect" | "onboarding" | "client";

export interface SiteOwner {
    businessId: string;
    businessName: string;
    stage: SiteOwnerStage;
    /** The newest usable scan (complete or partial), ISO. Null: none. */
    scannedAt: string | null;
    /** When the onboarding record was created (the signing). Null: no record. */
    signedAt: string | null;
}

const USABLE = new Set(["complete", "partial"]);
/**
 * Engagements that make a business someone already being set up or served.
 * Only these block signing (Angel, 15 Oct): a business marked "client" with
 * no plan in Oura (a "Quoted" row, The Pilates Collective) is signed here.
 */
const SIGNED = new Set(["signed", "active"]);

/**
 * The business that already owns this site, if any, and its stage. Live and
 * fixtures both read the businesses list, then the onboarding record. Only a
 * record the server says does not exist (`getOnboarding` answers null for a 404)
 * counts as none. A read that FAILED (a timeout, a 401, a 500) is a failure, not an
 * empty answer: it would otherwise return a made-up `prospect` or `client` stage
 * and send staff down the wrong path instead of showing a retryable outage
 * (Jack, #4285). Never throws.
 */
export async function findBusinessBySite(domain: string): Promise<ScanDraftResult<SiteOwner | null>> {
    if (scanDraftMode() === "absent") return ABSENT;
    const key = siteKey(domain);
    if (!key) return { ok: true, data: null };
    try {
        const businesses = await listBusinesses();
        const hit = businesses.find((b) => siteKey(b.domain) === key);
        if (!hit) return { ok: true, data: null };
        const latest = hit.latest_assessment;
        const scannedAt = latest && USABLE.has(latest.status) ? latest.captured_at : null;
        const record = await getOnboarding(hit.id);
        const stage: SiteOwnerStage = record
            ? record.scan.status === "complete"
                ? "client"
                : "onboarding"
            : SIGNED.has(hit.engagement_status ?? "")
              ? "client"
              : "prospect";
        return {
            ok: true,
            data: { businessId: hit.id, businessName: record?.businessName || hit.business_name, stage, scannedAt, signedAt: record?.createdAt ?? null },
        };
    } catch (err) {
        return failure(err, "The business could not be read.", "findBusinessBySite");
    }
}

/** A prospect that lives only as a New scan draft so far: drafted, or its first scan started. */
export interface ProspectDraft {
    draftId: string;
    /** Null until the scan creates the business (fixtures; live creates it at the start). */
    businessId: string | null;
    businessName: string;
    state: "drafted" | "scanning";
    /** ISO: when it was drafted, or when its scan started. */
    since: string;
}

/**
 * The prospect draft on this site, if any (JOURNEY-SPEC §2.3 item 2): a
 * started scan first (fixtures list them; live has the business row by then),
 * else an open New scan draft. Onboarding and edit drafts are not prospects.
 */
export async function findProspectDraft(domain: string): Promise<ScanDraftResult<ProspectDraft | null>> {
    if (scanDraftMode() === "absent") return { ok: true, data: null };
    const key = siteKey(domain);
    if (!key) return { ok: true, data: null };
    const prospectPurpose = (p: ScanDraftPurpose | null | undefined) => !p || p === "new_scan";
    const started = await listStartedScanDrafts();
    const run = started.ok ? started.data.find((d) => d.kind === "new_scan" && prospectPurpose(d.purpose) && siteKey(d.domain) === key) : undefined;
    if (run) {
        return { ok: true, data: { draftId: run.draftId, businessId: run.businessId, businessName: run.businessName || key, state: "scanning", since: run.startedAt } };
    }
    const open = await findOpenScanDraft({ domain: key });
    if (!open.ok) return open;
    const d = open.data;
    if (!d || d.kind !== "new_scan" || !prospectPurpose(d.purpose)) return { ok: true, data: null };
    return {
        ok: true,
        data: { draftId: d.id, businessId: d.businessId, businessName: d.business.businessName || key, state: "drafted", since: d.createdAt },
    };
}

/**
 * Whether a recipe update is on offer for a business, and how many of its 50
 * it would change. Live: `recipeUpdate` on GET /businesses/{id}. Its `changed`
 * is null until the backend has counted it; then an open recipe_change draft's
 * count is used, else no banner. A backend without the key falls back to the
 * open recipe_change draft.
 */
export function recipeUpdateFor(businessId: string): Promise<ScanDraftResult<{ changed: number } | null>> {
    return route(
        () => scanDraftMock.recipeOffer(businessId),
        async () => {
            try {
                const res = await axios.get(`${AEO_BASE}/businesses/${enc(businessId)}`, { headers: authHeaders() });
                const body = unwrap(res.data);
                if (isBody(body) && "recipeUpdate" in body) {
                    const update = body.recipeUpdate;
                    if (!isBody(update)) return { ok: true, data: null };
                    if (typeof update.changed === "number") return { ok: true, data: update.changed > 0 ? { changed: update.changed } : null };
                }
            } catch (err) {
                return failure(err, "The business could not be read.", "recipeUpdateFor");
            }
            return openRecipeChange(businessId);
        },
    );
}

/** The changed-row count of the business's open recipe_change draft, if it has one. */
async function openRecipeChange(businessId: string): Promise<ScanDraftResult<{ changed: number } | null>> {
    const open = await findOpenScanDraft({ businessId });
    if (!open.ok) return open;
    const draft = open.data;
    if (draft?.kind === "recipe_change" && draft.recipeChange) {
        // Kept all: the current 50 stay, so there is nothing left to offer.
        if (draft.recipeChange.mode === "keep_all") return { ok: true, data: null };
        return { ok: true, data: { changed: draft.recipeChange.rows.filter((r) => !r.same).length } };
    }
    return { ok: true, data: null };
}
